import type { ReactNode } from 'react';
import { Button, Card } from './primitives';
import './DraftCard.css';

/**
 * Brain chat draft card. The DraftCard renders a Brain-proposed
 * artifact with type-specific layout + an approve/dismiss CTA.
 *
 * Sprint 4.5d ports web1's 9 draft variants
 * (apps/web/src/components/CoachBrain/drafts/) into a single
 * type-routed renderer. Each variant lives in DRAFT_RENDERERS below
 * so the BrainChatPanel doesn't need to switch on the type itself.
 *
 * Variants ported:
 *   - draft_session_plan / DraftSessionPlan
 *   - draft_drill        / DraftDrill
 *   - draft_curriculum   / DraftCurriculum
 *   - draft_principle    / DraftPrinciple
 *   - draft_dev_note     / DraftDevNote
 *   - draft_memory       / DraftMemory
 *   - draft_player_review / DraftPlayerReview
 *   - draft_player_grade  / DraftPlayerGrade
 *   - draft_drill_grade   / DraftDrillGrade
 *   - draft_session_review_text / DraftSessionReviewText
 */
interface DraftCardProps {
  type: string;
  payload: unknown;
  status?: 'pending' | 'approved' | 'dismissed';
  /** When true, disable the Save button + show a wait state. Caller-driven
   *  so async commits don't render twice or race. */
  approving?: boolean;
  /** When true, Save is disabled because no commit path exists for this
   *  draft type yet. UI shows the hint below the disabled button so the
   *  coach knows where to go instead of clicking a dead button. */
  approveDisabled?: boolean;
  approveDisabledReason?: string;
  onApprove: () => void;
  onDismiss: () => void;
}

interface DraftMeta {
  /** Short uppercase label shown in the head — e.g. "session plan". */
  label: string;
  /** Render the body. Receives the typed payload. */
  render: (payload: Record<string, unknown>) => ReactNode;
  /** Theme tag — drives the accent strip on the left edge. */
  theme: 'session' | 'drill' | 'curriculum' | 'principle' | 'note' | 'memory' | 'review' | 'grade';
}

const DRAFT_RENDERERS: Record<string, DraftMeta> = {
  draft_drill_setup: {
    label: 'drill setup',
    theme: 'drill',
    render: (p) => {
      const pitch = String(p.pitch_view ?? 'full').replace(/_/g, ' ');
      const target = Number(p.target_player_count ?? 0);
      const players = Array.isArray(p.role_players) ? (p.role_players as Array<Record<string, unknown>>) : [];
      const cones = Array.isArray(p.cones) ? (p.cones as Array<Record<string, unknown>>) : [];
      const arrows = Array.isArray(p.arrows) ? (p.arrows as Array<Record<string, unknown>>) : [];
      const rationale = (p.rationale ?? '') as string;
      const organisation = (p.organisation ?? '') as string;
      const coachingPoints = Array.isArray(p.coaching_points)
        ? (p.coaching_points as string[])
        : [];
      const counts: Record<string, number> = {};
      for (const pl of players) {
        const rid = String(pl.role_id ?? 'unknown');
        counts[rid] = (counts[rid] ?? 0) + 1;
      }
      const roleSummary = Object.entries(counts)
        .map(([rid, n]) => `${n} ${rid}${n === 1 ? '' : 's'}`)
        .join(' · ');
      return (
        <>
          <h3 className="draft-body__title">Drill setup</h3>
          <p className="draft-body__meta">
            Pitch: {pitch}{target > 0 ? ` · target ${target} players` : ''}
          </p>
          {roleSummary && <p className="draft-body__desc">{roleSummary}</p>}
          {(cones.length > 0 || arrows.length > 0) && (
            <p className="draft-body__desc">
              {cones.length > 0 && `${cones.length} piece${cones.length === 1 ? '' : 's'} equipment`}
              {cones.length > 0 && arrows.length > 0 && ' · '}
              {arrows.length > 0 && `${arrows.length} arrow${arrows.length === 1 ? '' : 's'}`}
            </p>
          )}
          {rationale && (
            <p className="draft-body__desc" style={{ fontStyle: 'italic', opacity: 0.85 }}>
              {rationale}
            </p>
          )}
          {organisation && (
            <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              <div style={{ fontSize: 10, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Organisation</div>
              {organisation}
            </div>
          )}
          {coachingPoints.length > 0 && (
            <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: 6, fontSize: 12, lineHeight: 1.5 }}>
              <div style={{ fontSize: 10, opacity: 0.6, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 4 }}>Coaching points</div>
              <ul style={{ margin: 0, padding: '0 0 0 16px' }}>
                {coachingPoints.map((cp, i) => <li key={i}>{cp}</li>)}
              </ul>
            </div>
          )}
        </>
      );
    },
  },

  draft_session_plan: {
    label: 'session plan',
    theme: 'session',
    render: (p) => {
      const title = (p.session_title ?? p.title ?? 'Untitled session') as string;
      const drills = Array.isArray(p.drills) ? (p.drills as Array<Record<string, unknown>>) : [];
      const totalDuration = drills.reduce(
        (sum, d) => sum + Number(d.duration_mins ?? 0),
        0,
      );
      return (
        <>
          <h3 className="draft-body__title">{title}</h3>
          <p className="draft-body__meta">
            {drills.length} drill{drills.length === 1 ? '' : 's'} · {totalDuration} min total
          </p>
          {drills.length > 0 && (
            <ul className="draft-body__drill-list">
              {drills.slice(0, 6).map((d, i) => (
                <li key={i}>
                  <span className="draft-body__drill-name">{String(d.name ?? 'Drill')}</span>
                  <span className="draft-body__drill-dur">{Number(d.duration_mins ?? 0)}m</span>
                </li>
              ))}
              {drills.length > 6 && (
                <li className="draft-body__drill-more">+ {drills.length - 6} more</li>
              )}
            </ul>
          )}
        </>
      );
    },
  },

  draft_drill: {
    label: 'drill',
    theme: 'drill',
    render: (p) => {
      const name = (p.name ?? 'Untitled drill') as string;
      const description = (p.description ?? '') as string;
      const duration = Number(p.duration_mins ?? 0);
      return (
        <>
          <h3 className="draft-body__title">{name}</h3>
          {duration > 0 && <p className="draft-body__meta">{duration} min</p>}
          {description && <p className="draft-body__desc">{description}</p>}
        </>
      );
    },
  },

  draft_curriculum: {
    label: 'season plan',
    theme: 'curriculum',
    render: (p) => {
      // Payload is a CurriculumPlan: { total_weeks, what_to_expect, phase_plans[] }.
      // (Pre-fix this read `p.phases`/`ph.name`, which never matched the real shape.)
      const phases = Array.isArray(p.phase_plans) ? (p.phase_plans as Array<Record<string, unknown>>) : [];
      const totalWeeks = Number(p.total_weeks ?? 0);
      const whatToExpect = (p.what_to_expect ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">Season plan</h3>
          <p className="draft-body__meta">
            {phases.length} phase{phases.length === 1 ? '' : 's'}
            {totalWeeks > 0 ? ` · ${totalWeeks} week${totalWeeks === 1 ? '' : 's'}` : ''}
          </p>
          {whatToExpect && (
            <p className="draft-body__desc" style={{ fontStyle: 'italic', opacity: 0.85 }}>
              {whatToExpect}
            </p>
          )}
          {phases.length > 0 && (
            <ul className="draft-body__phases">
              {phases.slice(0, 5).map((ph, i) => {
                const rationale = (ph.rationale ?? '') as string;
                const weeklies = Array.isArray(ph.weekly_topics) ? ph.weekly_topics.length : 0;
                return (
                  <li key={i}>
                    <strong>{String(ph.phase ?? `Phase ${i + 1}`)}</strong>
                    {ph.theme ? <span> · {String(ph.theme)}</span> : null}
                    {weeklies > 0 ? <span style={{ opacity: 0.6 }}> · {weeklies}w</span> : null}
                    {rationale ? (
                      <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2, fontStyle: 'italic' }}>{rationale}</div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      );
    },
  },

  draft_principle: {
    label: 'principle',
    theme: 'principle',
    render: (p) => {
      const text = (p.text ?? '') as string;
      const scope = (p.scope ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">Principle</h3>
          {scope && <p className="draft-body__meta">scope · {scope}</p>}
          <p className="draft-body__quote">&ldquo;{text}&rdquo;</p>
        </>
      );
    },
  },

  draft_tactics_pattern: {
    label: 'tactics pattern',
    theme: 'principle',
    render: (p) => {
      const intent = (p.intent as string) ?? 'Tactics pattern';
      const home = (p.home_formation as string) ?? '4-3-3';
      const away = (p.away_formation as string) ?? '';
      const homeSpots = Array.isArray(p.home_spots) ? p.home_spots.length : 0;
      const awaySpots = Array.isArray(p.away_spots) ? p.away_spots.length : 0;
      const arrows = Array.isArray(p.arrows) ? p.arrows.length : 0;
      return (
        <>
          <h3 className="draft-body__title">{intent}</h3>
          <p className="draft-body__meta">
            {home}
            {away ? ` vs ${away}` : ''}
            {' · '}
            {homeSpots} home
            {awaySpots > 0 ? ` · ${awaySpots} away` : ''}
            {arrows > 0 ? ` · ${arrows} arrow${arrows === 1 ? '' : 's'}` : ''}
          </p>
        </>
      );
    },
  },

  draft_player_briefs: {
    label: 'player briefs',
    theme: 'principle',
    render: (p) => {
      const side = (p.side as string) ?? 'home';
      const briefs = Array.isArray(p.briefs) ? p.briefs.length : 0;
      return (
        <>
          <h3 className="draft-body__title">Player briefs ({side})</h3>
          <p className="draft-body__meta">{briefs} brief{briefs === 1 ? '' : 's'} ready</p>
        </>
      );
    },
  },

  draft_dev_note: {
    label: 'dev note',
    theme: 'note',
    render: (p) => {
      const text = (p.text ?? '') as string;
      const playerName = (p.player_name ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">{playerName ? `Note · ${playerName}` : 'Development note'}</h3>
          <p className="draft-body__desc">{text}</p>
        </>
      );
    },
  },

  draft_memory: {
    label: 'memory',
    theme: 'memory',
    render: (p) => {
      const text = (p.text ?? '') as string;
      const scope = (p.scope ?? 'team') as string;
      return (
        <>
          <h3 className="draft-body__title">Memory · {scope}</h3>
          <p className="draft-body__desc">{text}</p>
        </>
      );
    },
  },

  draft_player_review: {
    label: 'player review',
    theme: 'review',
    render: (p) => {
      const playerName = (p.player_name ?? 'Player') as string;
      const corners = (p.corners ?? {}) as Record<string, string>;
      const summary = (p.summary ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">{playerName}</h3>
          {summary && <p className="draft-body__desc">{summary}</p>}
          {Object.keys(corners).length > 0 && (
            <ul className="draft-body__corners">
              {Object.entries(corners).slice(0, 4).map(([k, v]) => (
                <li key={k}><strong>{k.replace('_', '/')}</strong>: {v}</li>
              ))}
            </ul>
          )}
        </>
      );
    },
  },

  draft_player_grade: {
    label: 'player grade',
    theme: 'grade',
    render: (p) => {
      const playerName = (p.player_name ?? 'Player') as string;
      const score = Number(p.score ?? 0);
      const note = (p.note ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">{playerName} · {score}/100</h3>
          {note && <p className="draft-body__desc">{note}</p>}
        </>
      );
    },
  },

  draft_drill_grade: {
    label: 'drill grade',
    theme: 'grade',
    render: (p) => {
      const drillName = (p.drill_name ?? 'Drill') as string;
      const sentiment = (p.sentiment ?? '?') as string;
      const note = (p.note ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">{drillName} · {sentiment}</h3>
          {note && <p className="draft-body__desc">{note}</p>}
        </>
      );
    },
  },

  draft_session_review_text: {
    label: 'session narrative',
    theme: 'review',
    render: (p) => {
      const text = (p.text ?? p.narrative ?? '') as string;
      return (
        <>
          <h3 className="draft-body__title">Post-session narrative</h3>
          <p className="draft-body__desc">{text}</p>
        </>
      );
    },
  },
};

function fallbackRender(type: string, payload: unknown): { meta: DraftMeta; rendered: ReactNode } {
  const p = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
  const fallbackTitle =
    (typeof p.title === 'string' && p.title) ||
    (typeof p.name === 'string' && p.name) ||
    (typeof p.text === 'string' && p.text.slice(0, 200)) ||
    type;
  return {
    meta: { label: type.replace(/^draft_/, '').replaceAll('_', ' '), theme: 'note', render: () => null },
    rendered: <p className="draft-body__desc">{fallbackTitle}</p>,
  };
}

export function DraftCard({
  type,
  payload,
  status = 'pending',
  approving = false,
  approveDisabled = false,
  approveDisabledReason,
  onApprove,
  onDismiss,
}: DraftCardProps) {
  const settled = status !== 'pending';
  const meta = DRAFT_RENDERERS[type];
  let body: ReactNode;
  let label: string;
  let theme: DraftMeta['theme'];
  if (meta) {
    label = meta.label;
    theme = meta.theme;
    const safe = (payload && typeof payload === 'object' ? payload : {}) as Record<string, unknown>;
    body = meta.render(safe);
  } else {
    const fb = fallbackRender(type, payload);
    label = fb.meta.label;
    theme = fb.meta.theme;
    body = fb.rendered;
  }

  return (
    <Card variant="glass" className="draft-card" data-status={status} data-theme={theme} data-testid="draft-card">
      <div className="draft-card__head">
        <span className="draft-card__type">{label}</span>
        {settled && <span className="draft-card__status">{status}</span>}
      </div>
      <div className="draft-card__body">{body}</div>
      {!settled && (
        <div className="draft-card__actions" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 8 }} data-testid="draft-card-actions">
            <Button
              variant="primary"
              size="touchline"
              onClick={onApprove}
              disabled={approving || approveDisabled}
              data-testid="draft-approve"
            >
              {approving ? 'Saving…' : 'Save'}
            </Button>
            <Button variant="glass" size="touchline" onClick={onDismiss} data-testid="draft-dismiss">Dismiss</Button>
          </div>
          {approveDisabled && approveDisabledReason && (
            <span style={{ fontSize: 11, opacity: 0.7, fontStyle: 'italic' }}>
              {approveDisabledReason}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}
